import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateManagerPlan } from '../../shared/api/planning.api'
import { toast } from '../../shared/ui/toastStore'

export type PlanningPatchVars = {
  managerId: string
  periodMonth: string
  planAmount?: number
  factAmount?: number
  akbPlanCount?: number
  akbFactCount?: number
}

/**
 * Сохранение плана, факта и АКБ менеджера (PATCH /planning/:managerId).
 *
 * Возвращает функцию, которая резолвится в true при успехе и в false при
 * отказе сервера. Отказ показывается тостом с текстом сервера: раньше у
 * мутации не было onError, и 422 («„План“: не больше 999999999999») терялась
 * молча — в поле оставалось число, которое на самом деле не сохранилось.
 * Вынесено из PlanningPage, чтобы страница не перешагнула лимит 500 строк.
 */
export function usePlanningUpsert(): (vars: PlanningPatchVars) => Promise<boolean> {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ managerId, periodMonth, ...fields }: PlanningPatchVars) =>
      updateManagerPlan(managerId, periodMonth as `${number}-${number}`, {
        ...(fields.planAmount !== undefined ? { planAmount: fields.planAmount } : {}),
        ...(fields.factAmount !== undefined ? { factAmount: fields.factAmount } : {}),
        ...(fields.akbPlanCount !== undefined ? { akbPlanCount: fields.akbPlanCount } : {}),
        ...(fields.akbFactCount !== undefined ? { akbFactCount: fields.akbFactCount } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['planning'] })
      void queryClient.invalidateQueries({ queryKey: ['planning-chart'] })
    },
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
        ?.error?.message
      toast.error(msg ?? 'Не удалось сохранить план или факт — попробуйте ещё раз')
    },
  })
  return (vars) =>
    mutation.mutateAsync(vars).then(
      () => true,
      () => false,
    )
}
